enum Order {
  ASC,
  DESC,
}

export interface Enumerable<T> {
  /**
   * Converte o objeto em array
   */
  toArray(): T[];
}

export interface InterOrdenable<T> extends Enumerable<T> {
  thenBy<TResult>(selector: (item: T) => TResult): InterOrdenable<T>;
  thenByDescending<TResult>(selector: (item: T) => TResult): InterOrdenable<T>;
}

export interface InterGrouped<Key, Item> extends Enumerable<Item> {
  key: Key;
  items: Item[];
}

class Grouped<Key, Item> implements InterGrouped<Key, Item> {
  key: Key;
  items: Item[];

  toArray(): Item[] {
    return this.items;
  }

  private static keyComparer(a: any, b: any): boolean {
    if (typeof a === "object" && a !== null) {
      for (const prop in b) {
        if (
          typeof a[prop] === "object" &&
          a[prop] !== null &&
          !this.keyComparer(a[prop], b[prop])
        )
          return false;
        else if (a[prop] != b[prop]) return false;
      }
      return true;
    }

    return a == b;
  }

  static fromArray<Key, Item>(
    origin: Item[],
    selector: (item: Item) => Key
  ): InterGrouped<Key, Item>[] {
    return origin
      .select(selector)
      .distinct()
      .select(
        (x) =>
          new Grouped(
            x,
            origin.where((y) => this.keyComparer(selector(y), x))
          )
      );
  }

  private constructor(key: Key, items: Item[]) {
    this.key = key;
    this.items = items;
  }
}

class Ordenable<T> implements InterOrdenable<T> {
  private origin: T[];
  private comparer: ((a: T, b: T) => number)[] = [];

  private compare(a: T, b: T, n = 0): number {
    const index = this.comparer[n](a, b);
    if (index != 0 || n == this.comparer.length - 1) return index;
    return this.compare(a, b, ++n);
  }

  toArray(): T[] {
    return this.origin.sort((a, b) => this.compare(a, b));
  }
  thenBy<TResult>(selector: (item: T) => TResult): InterOrdenable<T> {
    this.comparer.push((a, b) =>
      selector(a) > selector(b) ? 1 : selector(a) < selector(b) ? -1 : 0
    );
    return this;
  }
  thenByDescending<TResult>(selector: (item: T) => TResult): InterOrdenable<T> {
    this.comparer.push((a, b) =>
      selector(a) < selector(b) ? 1 : selector(a) > selector(b) ? -1 : 0
    );
    return this;
  }

  constructor(origin: T[], order: Order, selector: (item: T) => any) {
    this.origin = origin;
    switch (order) {
      case Order.ASC:
        this.comparer.push((a, b) =>
          selector(a) > selector(b) ? 1 : selector(a) < selector(b) ? -1 : 0
        );
        break;
      case Order.DESC:
        this.comparer.push((a, b) =>
          selector(a) < selector(b) ? 1 : selector(a) > selector(b) ? -1 : 0
        );
        break;
    }
  }
}

declare global {
  interface Array<T> {
    /**
     * Cálcula a média aritmética de um array
     * @param selector Função lambda para obter o valor a ser cálculado a média
     */
    average(selector: (item: T) => number): number;
    /**
     * Cálcula a média aritmética de um array
     * @param selector Função lambda para obter o valor a ser cálculado a média
     */
    average(selector: (item: T, index: number) => number): number;
    /**
     * Adiciona um item no array
     * @param item Item a ser adicionado na lista
     */
    add(item: T): number;
    /**
     * Limpa todos os elementos da lista
     */
    clear(): void;
    distinct(): T[];
    groupBy<Key>(selector: (item: T) => Key): InterGrouped<Key, T>[];
    /**
     * Adiciona um elemento em uma determinada posição do array
     * @param index Índice onde o elemento será inserido
     * @param item Elemento a ser inserido no array
     */
    insert(index: number, item: T): void;
    /**
     * Ordena um array de forma crescente
     * @param selector Função para o obter o valor parametro de ordenação
     */
    orderBy<TResult>(selector: (item: T) => TResult): InterOrdenable<T>;
    /**
     * Ordena um array de forma decrescente
     * @param selector Função para o obter o valor parametro de ordenação
     */
    orderByDescending<TResult>(
      selector: (item: T) => TResult
    ): InterOrdenable<T>;
    /**
     * Remove o elemento determinado do array
     * @param item Elemento a ser removido
     */
    remove(item: T): void;
    /**
     * Remove o elemento referente à posição informada
     * @param index Índice do elemento a ser removido
     */
    removeAt(index: number): void;
    select<Type>(predicate: (item: T) => Type): Type[];
    select<Type>(predicate: (item: T, index: number) => Type): Type[];
    sum(selector: (item: T) => number): number;
    sum(selector: (item: T, index: number) => number): number;
    where(condition: (item: T) => boolean): T[];
    where(condition: (item: T, index: number) => boolean): T[];
  }
}

Array.prototype.add = Array.prototype.push;
Array.prototype.clear = function<T>(this: T[]) {
  this.splice(0, this.length);
};
Array.prototype.removeAt = function<T>(this: T[], index: number) {
  this.splice(index, 1);
};
Array.prototype.remove = function<T>(this: T[], item: T) {
  this.removeAt(this.indexOf(item));
};
Array.prototype.select = Array.prototype.map;
Array.prototype.where = Array.prototype.filter;

function objectComparer<T>(a: T, b: T): boolean {
  if (typeof a === "object" && a !== null) {
    for (const prop in a) {
      if (
        typeof a[prop] === "object" &&
        a[prop] !== null &&
        !objectComparer(a[prop], b[prop])
      ) {
        return false;
      } else if (a[prop] != b[prop]) return false;
    }
  }

  return true;
}

function distinctComparer<T>(a: T, self: T[]): number {
  if (typeof a === "object" && a !== null) {
    return self.findIndex((b) => {
      for (const prop in b) {
        if (
          typeof a[prop] === "object" &&
          a[prop] !== null &&
          !objectComparer(a[prop], b[prop])
        ) {
          return false;
        } else if (a[prop] != b[prop]) return false;
      }
      return true;
    });
  }

  return self.indexOf(a);
}
Array.prototype.distinct = function<T>(this: T[]) {
  return this.filter(
    (item, index, self) => distinctComparer(item, self) === index
  );
};
Array.prototype.orderBy = function<T, TResult>(
  this: T[],
  selector: (item: T) => TResult
) {
  return new Ordenable(this, Order.ASC, selector);
};
Array.prototype.orderByDescending = function<T, TResult>(
  this: T[],
  selector: (item: T) => TResult
) {
  return new Ordenable(this, Order.DESC, selector);
};
Array.prototype.groupBy = function<Key, T>(
  this: T[],
  selector: (element: T) => Key
) {
  return Grouped.fromArray(this, selector);
};
